import { computed, reactive, readonly } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { cloneDeep, isEqual, isNil, uniqBy } from 'lodash'
import buildURL from 'axios/lib/helpers/buildURL'

import events$ from '@/services/Events'

import { isRequired, MissingParameterError } from '@/utils/CustomErrors'

import { ReactivityUtil } from '@/utils/Reactivity'

import useApplication from '@/hooks/useApplication'
import useBackend from '@/hooks/useBackend'
import useDistribution from '@/hooks/useDistribution'
import useError from '@/hooks/useError'
import useLogger from '@/hooks/useLogger'
import useNotifications from '@/hooks/useNotifications'
import usePerson from '@/hooks/usePerson'
import useProduct from '@/hooks/useProduct'
import useSession from '@/hooks/useSession'
import useStatus from '@/hooks/useStatus'

import config from '@/config'
import {
  FREE_MOVEMENT_OF_PERSONS_COUNTRIES,
  DEFAULT_DISTRIBUTION_PARTNER_NUMBER,
  DISTRIBUTION_PARTNER_TYPE,
} from '@/config/constants'
import { EVENT_BASKET, EVENT_PRODUCT } from '@/config/events'

// Hooks
const { emitMessages } = useApplication()
const { createDistributionPartners, getDistributionPartnerNumber } = useDistribution()
const { logInfo } = useLogger()
const { setSession } = useSession()
const { isOnboardingSecured } = useStatus()
const { setup, doGet, doPost, doPut, getBaseUrl } = useBackend()
const { createErrorResponse } = useError()
const { addErrorNotification } = useNotifications()

setup('/ovpv3', config.microserviceBaseUrl, {
  responseSuccess: response => {
    emitMessages(response.data.infoMessages)
    return response.data
  },
  responseError: error => {
    const __error = createErrorResponse(error)

    if (__error.status === 999) {
      console.error('error', error)
    } else {
      if (__error?.infoMessages[0]?.errorCode) {
        addErrorNotification(__error?.infoMessages[0])
      }
    }

    throw __error
  },
})

// Data
const internalBasket = reactive({})
const basket = readonly(internalBasket)

// Computed
const basketTotal = computed(() => {
  const { personsWithDetails } = usePerson()
  const __total = personsWithDetails.value
    .reduce((total, person) => {
      total += person.total
      return total
    }, 0)
    .toFixed(2)

  return parseFloat(__total)
})

const contractData = computed(() => {
  return {
    address: basket.address,
    contractStartDate: basket.contractStartDate,
  }
})

/**
 * isStaticBasket returns true for baskets of existing customers with closed products in the contract
 */
const isStaticBasket = computed(() => {
  if (basket.existingCustomer === undefined || basket.dynamic === undefined) return false
  return basket.existingCustomer && !basket.dynamic
})

/**
 * This value can change based on year
 */
const voc = computed(() => {
  return basket.contractStartDate.substr(0, 4) === '2023' ? '5.10' : '7.35'
})

// methods
async function createBasket(payload = isRequired('payload')) {
  const response = await doPost('/baskets', payload)
  return response.data
}

async function updateBasket(payload = isRequired('basket'), params = {}) {
  const { basketId, jwToken } = payload
  if (!basketId) throw new MissingParameterError('basketId')
  if (!jwToken) throw new MissingParameterError('jwToken')

  const path = buildURL(`/baskets/${basketId}`, { jwToken, ...params })
  const response = await doPut(path, payload)
  return response.data
}

/**
 * Simulates a basketUpdate (getting products, prices, etc.) but doesn't save to the database
 *
 * @param {Basket} payload  Current basket
 * @params {Object} params Extended put url parameters
 * @returns {Promise}
 */
async function simulateUpdateBasket(payload = isRequired('basket'), params = {}) {
  const { basketId, jwToken } = payload
  if (!basketId) throw new MissingParameterError('basketId')
  if (!jwToken && isOnboardingSecured.value) throw new MissingParameterError('jwToken')

  const path = buildURL(`/alva/updateProducts`, { jwToken, ...params })
  const response = await doPut(path, payload)
  return response.data
}

async function createDefaultBasket(params) {
  const contractStartDate = (await doGet('/contractStartDate/default')).data.contractStartDate

  return {
    contractStartDate,
    basketId: uuidv4(),
    distributionPartners: createDistributionPartners(DEFAULT_DISTRIBUTION_PARTNER_NUMBER),
    persons: [],
    //channel: CHANNEL.OVP,
    language: 'DE',
    ...params,
  }
}

async function createOvpBasket(payload) {
  const createdBasket = await createBasket(payload)

  // request failed, abort!
  if (!createdBasket) return

  setSession(createdBasket)
  setBasket(createdBasket)

  return basket
}

/**
 * Merges the current basket state from the backend with the passed payload, creates/updates the basket in the backend
 * and sets the new basket and session.
 * @param payload
 * @param options
 *  simulate: whether to make a saving call or only a simulated call to the backend
 * @param params
 * @returns {Promise<*>}
 */
async function updateOvpBasket(payload, options, params) {
  const __options = Object.assign({}, { simulate: false }, options)
  const __basketData = Object.assign({}, cloneDeep(basket), payload)

  let updatedBasket
  if (isTemporaryBasket(__basketData)) {
    updatedBasket = await createBasket(__basketData)
  } else if (__options.simulate) {
    updatedBasket = await simulateUpdateBasket(__basketData, params)
  } else {
    updatedBasket = await updateBasket(__basketData, params)
  }

  if (!updatedBasket) throw new Error('failed to create basket')

  if (!__options.simulate) {
    setSession(updatedBasket)
    setBasket(updatedBasket)
  }

  return updatedBasket
}

async function fetchBasket(payload) {
  const { basketId, jwToken } = payload

  let response
  if (basketId && jwToken) {
    response = await doGet(`/baskets/${basketId}`, { jwToken, timestamp: Date.now() })
  } else {
    response = await doGet(`/baskets`, payload)
  }

  return response.data
}

/**
 * @param {String} token  Reentry token from the basket
 * @returns {Promise}
 */
async function sendSMSTan(token) {
  return await doGet('/baskets/smstan', { token }).then(response => response.data)
}

function sendSMSTanOnLogin({ basketId = isRequired('basketId'), mobileNumber = isRequired('mobileNumber') }) {
  const url = buildURL(`/mobile-number/${basketId}/send-sms-tan`, { mobileNumber })
  return doPost(url, undefined).then(response => response.data)
}

function verifySMSTanOnLogin({
  basketId = isRequired('basketId'),
  mobileNumberVerificationSmsTan = isRequired('mobileNumberVerificationSmsTan'),
}) {
  const url = buildURL(`/mobile-number/${basketId}/verify-sms-tan`, { mobileNumberVerificationSmsTan })
  return doGet(url, undefined).then(response => response.data)
}

async function fetchDistributionPartner(
  {
    contractStartDate,
    distributionPartners,
    //distributionPartnerContact = isRequired('distributionPartnerContact'),
    //distributionPartnerInfo = isRequired('distributionPartnerInfo'),
    language = isRequired('language'),
  },
  partnerNumber
) {
  const data = {}

  const distributionPartnerContact = getDistributionPartnerNumber(
    distributionPartners,
    DISTRIBUTION_PARTNER_TYPE.ABSCHLUSS
  )
  const distributionPartnerInfo = getDistributionPartnerNumber(
    distributionPartners,
    DISTRIBUTION_PARTNER_TYPE.ABSCHLUSS
  )

  if (contractStartDate) {
    const _base = await doGet(`/distributionpartner/${distributionPartnerInfo}`, { language, contractStartDate })
    if (_base) Object.assign(data, { base: _base.data })

    const _info = await doGet(`/distributionpartner/${distributionPartnerInfo}/organizationName`, {
      language,
      contractStartDate,
    })
    if (_info) Object.assign(data, { info: _info.data })
  }

  const _contact = await doGet(`/distributionpartner/${distributionPartnerContact}/serviceCenter`, { partnerNumber })
  if (_contact) Object.assign(data, { contact: _contact.data })

  return data
}

function getOverviewPdfUrl() {
  if (!basket.basketId || !basket.jwToken) {
    return null
  }

  return buildURL(`${getBaseUrl.value}/baskets/${basket.basketId}/overview.pdf`, {
    jwToken: basket.jwToken,
  })
}

function getBasketByPartnerNumber({ partnerNumber = isRequired('partnerNumber'), uid = isRequired('uid') }) {
  const url = buildURL(`/userregistry/get-family`, { partnerNumber, uid })
  return doGet(url, {}).then(response => response.data)
}

function hasContractStartChangedAfterCancellation(payload, __basket) {
  return payload.contractStartDate !== __basket.contractStartDate && !isNil(__basket.cancellationAnswer)
}

async function customizeContract(payload) {
  // FLEX-993 delete cancellation data if contract start date changed after cancellation was answered
  const updatePayload = { ...payload }
  if (hasContractStartChangedAfterCancellation(updatePayload, basket)) {
    updatePayload.cancellationAnswer = null
  }

  return await updateOvpBasket(updatePayload)
}

//TODO here or should this be in usePerson? I put it here since basket updates are here - Toni
async function customizePerson(payload) {
  const { formData, person } = payload

  const personData = Object.assign({}, person.personData, formData)

  const response = await updateBasketForPerson(person.personId, { personData })
  return updateOvpBasket(response)
}

async function customizeProduct(payload, source) {
  const { selectProductOption } = useProduct()
  const { categoryId, personId, productId, option } = payload

  let _basket = await selectProductOption(personId, productId, option)
  _basket = await updateOvpBasket(_basket)

  events$.emit(EVENT_PRODUCT.CHANGED, {
    basket: _basket,
    categoryId,
    option,
    personId,
    productId,
    source,
  })

  return _basket
}

function removePersonsWithoutProductFromOvpBasket(payload) {
  const { getSelectedProductsOfPerson } = useProduct()

  const immutablePersons = payload.existingCustomer ? payload.persons.filter(p => p.partnerNumber) : []
  // @todo probably should check differently if person has products?
  const personsWithProducts = payload.persons
    .filter(p => !p.partnerNumber)
    .filter(p => getSelectedProductsOfPerson(p.products.products).length > 0)
  const payloadPersons = [...immutablePersons, ...personsWithProducts]

  return updateOvpBasket({ persons: payloadPersons })
}

async function updateLanguage(locale) {
  const basketLanguage = locale.toUpperCase()
  const isReentry = basket.mobile?.includes('*')

  if (isTemporaryBasket()) {
    internalBasket.language = basketLanguage
  } else if (basket.language !== basketLanguage && !isReentry) {
    await updateOvpBasket({ language: basketLanguage })
  }
}

function isTemporaryBasket(__basket) {
  const __basketData = __basket || basket
  return !__basketData.createDate
}

function emptyBasket() {
  ReactivityUtil.resetData(internalBasket)
}

function setBasket(newBasket) {
  const originalBasket = cloneDeep(basket)

  let madeChange = false
  for (const key in newBasket) {
    if (!isEqual(newBasket[key], internalBasket[key])) {
      internalBasket[key] = newBasket[key]
      madeChange = true
    }
  }

  if (madeChange) {
    logInfo(['%cBASKET SET', 'color: green', basket])
    events$.emit(EVENT_BASKET.UPDATED, {
      basket,
      originalBasket,
    })
  }
}

// @todo why am I allowed to pass no basket here? also fix name shadowing.
function addProductDoctorToBasket(personId, productId, doctor, __basket = null) {
  const updatedBasket = mutateProductsInBasket(personId, productId, { basket: __basket })
  const product = updatedBasket.products.products[productId]
  product.doctor = doctor
  return updateBasketForDoctorPerson(personId, updatedBasket, doctor)
}

function addProductToOvpBasket(personId, productId, options = {}) {
  const { getProduct } = useProduct()
  const mode = options.mode ?? 'REPLACE'
  let doctor = options.doctor ?? null
  const __basket = options.basket ?? null

  const product = __basket
    ? __basket.persons.find(p => p.personId === personId).products.products[productId]
    : getProduct(personId, productId)

  if (!doctor) {
    doctor = product?.doctor
  }

  if (doctor) {
    return addProductDoctorToBasket(personId, productId, doctor, __basket)
  } else {
    const pBasket = mutateProductsInBasket(personId, productId, { basket: __basket, mode })
    return updateBasketForPerson(personId, pBasket, { shouldRemoveUnusedDoctors: true })
  }
}

async function removeProductFromOvpBasket(personId, productId) {
  const updatedBasket = mutateProductsInBasket(personId, productId, { mode: 'REMOVE' })

  const __basket = await updateBasketForPerson(personId, updatedBasket, { shouldRemoveUnusedDoctors: true })
  return updateOvpBasket(__basket)
}

function mutateProductsInBasket(personId, productId, options = {}) {
  const { getPerson } = usePerson()
  const mode = options.mode ?? 'REPLACE'
  const ___basket = options.basket ?? null

  const pBasket = ___basket ? ___basket.persons.find(p => p.personId === personId) : getPerson(personId)
  const products = pBasket.products.products

  const productKeys = Object.keys(products)
  const tmpBasket = {}

  switch (mode) {
    case 'ADD':
      productKeys.forEach(product => {
        tmpBasket[product] = Object.assign(products[product], {
          selected: productId === product ? true : products[product].selected,
        })
      })
      break

    case 'REMOVE':
      productKeys.forEach(product => {
        tmpBasket[product] = Object.assign(products[product], {
          selected: productId === product ? false : products[product].selected,
        })
      })
      break

    /**
     * replace existing selected products with the new one (apply rules)
     */
    case 'REPLACE':
      replaceProductInBasket(productId, personId, tmpBasket, products, productKeys)
      break
  }

  pBasket.products.products = tmpBasket

  return pBasket
}

function replaceProductInBasket(productId, personId, tmpBasket, products, productKeys) {
  const {
    categories,
    groups,
    getCategoryIdFromProduct,
    getGroupIdFromProduct,
    getProductsFromCategory,
    getProductsFromGroup,
  } = useProduct()

  let productsToReplace = []
  const categoryId = getCategoryIdFromProduct(productId)
  const category = categories.value[categoryId]
  const groupId = getGroupIdFromProduct(productId)
  const group = groups.value[groupId]

  const reducer = (acc, value) => {
    acc.push(value.productId)
    return acc
  }

  if (category?.exclusive) {
    productsToReplace = getProductsFromCategory(categoryId, personId).reduce(reducer, [])
  } else if (group?.exclusive) {
    productsToReplace = getProductsFromGroup(groupId, personId).reduce(reducer, [])
  }

  productKeys.forEach(product => {
    if (product === productId) {
      tmpBasket[product] = Object.assign({}, products[product], {
        selected: true,
      })
    } else if (productsToReplace.includes(product)) {
      tmpBasket[product] = Object.assign({}, products[product], {
        selected: false,
      })
    } else {
      tmpBasket[product] = products[product]
    }

    return tmpBasket[product]
  })
}

function addPersonDoctor(payload, productDoctor) {
  const index = payload.doctors.findIndex(d => d.zsrNumber === productDoctor.zsrNumber)
  if (index === -1) {
    const doctor = transferProductDoctor(productDoctor)
    payload.doctors.push(doctor)
  }
  return Promise.resolve(payload)
}

function transferProductDoctor(doctor) {
  const { address } = doctor
  return {
    country: address.country,
    doctorId: uuidv4(),
    name: [doctor.firstName, doctor.lastName].join(' ').trim(),
    street: [address.street, address.houseNumber].join(' ').trim(),
    town: [address.zipCode, address.town].join(' ').trim(),
    zsrNumber: doctor.zsrNumber,
  }
}

function updateBasketForPerson(personId, payload, options = {}) {
  const newBasket = options.basket || cloneDeep(basket)
  const { getPersonIndex } = usePerson()
  const index = getPersonIndex(personId)
  newBasket.persons[index] = Object.assign(newBasket.persons[index], payload)

  return options.shouldRemoveUnusedDoctors ? removeUnusedDoctors(newBasket) : Promise.resolve(newBasket)
}

function updateBasketForDoctorPerson(personId, payload, productDoctor) {
  return updateBasketForPerson(personId, payload)
    .then(removeUnusedDoctors)
    .then(_basket => addPersonDoctor(_basket, productDoctor))
}

function updateHealthquestionsForPerson(personId, payload) {
  const __basket = cloneDeep(basket)
  const { getPersonIndex } = usePerson()
  const personIndex = getPersonIndex(personId)
  const hqs = __basket.persons[personIndex].healthQuestionAnswers

  Object.keys(payload).forEach(hqId => {
    const hqIndex = hqs.findIndex(hq => hq.healthQuestionId === hqId)
    const hqData = __basket.persons[personIndex].healthQuestionAnswers[hqIndex]

    if (payload[hqId]?.doctors) {
      const doctors = payload[hqId].doctors.filter(doctor => !doctor.zsrNumber)
      attachDoctors(__basket, doctors)
    }

    __basket.persons[personIndex].healthQuestionAnswers[hqIndex] = Object.assign(hqData, payload[hqId])
  })

  return Promise.resolve(__basket)
}

/**
 * Attach doctors to the passed basket-object
 *
 * @param {Basket} __basket       Current basket (by reference!)
 * @param {Doctor[]} doctors    Array with doctors
 */
function attachDoctors(__basket, doctors) {
  doctors.forEach(doctor => {
    const index = __basket.doctors.findIndex(d => d.doctorId === doctor.doctorId)
    if (index >= 0) {
      __basket.doctors[index] = doctor
    } else {
      __basket.doctors.push(doctor)
    }
  })
}

/**
 * Cleanup basket of unused doctors (not linked to product or hq)
 *
 * @param {Basket} basket       Current basket
 * @returns {Promise<Basket>}   Returns basket with only used doctors
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
function removeUnusedDoctors(payload) {
  const updatedBasket = cloneDeep(payload)
  if (updatedBasket?.doctors?.length > 0) {
    const basketDoctors = updatedBasket.doctors.reduce((doctors, doctor) => {
      if (!doctor.zsrNumber) {
        doctors.push(doctor)
      } else {
        updatedBasket.persons.some(person => {
          if (!person.products) {
            return false
          }

          const products = Object.values(person.products.products)

          if (products.some(product => product.doctor?.zsrNumber === doctor.zsrNumber && product.selected)) {
            return doctors.push(doctor)
          }
          person.healthQuestionAnswers.some(question => {
            if (question.doctorIds?.length > 0 && question.doctorIds.includes(doctor.doctorId)) {
              return doctors.push(doctor)
            } else if (question?.treatmentCases?.length > 0) {
              return question.treatmentCases.some(treatment => {
                if (treatment.doctorIds?.length > 0 && treatment.doctorIds.includes(doctor.doctorId)) {
                  return doctors.push(doctor)
                }
              })
            }
          })
        })
      }
      return doctors
    }, [])
    updatedBasket.doctors = uniqBy(basketDoctors, 'doctorId')
  }

  return Promise.resolve(updatedBasket)
}

function hasPersonsNeedingPermit(basketPersons) {
  return basketPersons.some(person => {
    const movedToCH = person?.preInsurer?.currentKvgMovedToCH ?? false
    return movedToCH && !FREE_MOVEMENT_OF_PERSONS_COUNTRIES.includes(person.preInsurer.currentMovedToCHType)
  })
}

export default function useBasket() {
  return {
    // computeds
    basket,
    basketTotal,
    contractData, // computed instead of getContractData method
    voc,

    // various
    createDefaultBasket,
    isTemporaryBasket,
    isStaticBasket,
    getOverviewPdfUrl,
    hasPersonsNeedingPermit,
    updateHealthquestionsForPerson,

    // reads from be
    fetchBasket,
    fetchDistributionPartner,
    getBasketByPartnerNumber,
    verifySMSTanOnLogin,

    // writes to be
    addProductDoctorToBasket,
    addProductToOvpBasket,
    createOvpBasket,
    updateOvpBasket,
    updateLanguage,
    updateBasket,
    updateBasketForPerson,
    customizeContract,
    customizePerson,
    customizeProduct,
    removePersonsWithoutProductFromOvpBasket,
    removeProductFromOvpBasket,
    sendSMSTan,
    sendSMSTanOnLogin,

    // updates local basket
    emptyBasket,
    persistBasket: setBasket,
  }
}
